import { useStore } from '@nanostores/react'
import { atom } from 'nanostores'
import { Dialog as DialogPrimitives } from 'radix-ui'
import { Button } from './button'
import { DialogShell } from './dialog-shell'
import { Spinner } from './spinner'

export interface DialogData {
  title: string
  description: string
  button: string
  action?: () => void
  /**
   * Async primary action. The dialog manages the loading state itself and
   * locks dismissal while the promise is in flight; it does NOT close when
   * the promise settles — end the flow with `dialog.close()` or by opening
   * a follow-up dialog. Handle errors inside the action.
   */
  asyncAction?: () => Promise<void>
  closeButton?: boolean | { action: () => void }
}

interface State {
  isOpen: boolean
  isLoading: boolean
  dialog: DialogData
}

const initialState: State = {
  isOpen: false,
  isLoading: false,
  dialog: {
    title: '',
    description: '',
    button: '',
    closeButton: false,
  },
}

export const $dialog = atom<State>(initialState)

const setLoading = (isLoading: boolean) => {
  $dialog.set({
    ...$dialog.get(),
    isLoading,
  })
}

export const dialog = {
  open: (payload: DialogData) => {
    $dialog.set({
      dialog: payload,
      isOpen: true,
      isLoading: false,
    })
  },
  close: () => {
    $dialog.set({
      ...$dialog.get(),
      isOpen: false,
      isLoading: false,
    })
  },
}

export function Dialog() {
  const state = useStore($dialog)

  const handleButtonClick = () => {
    const { action, asyncAction } = state.dialog
    if (asyncAction) {
      setLoading(true)
      asyncAction()
        .finally(() => setLoading(false))
        // Errors are the action's responsibility (see DialogData.asyncAction);
        // this is a safety net so a slipped rejection stays visible without
        // surfacing as an unhandled rejection in every consuming app.
        .catch((error: unknown) => console.error(error))
    } else {
      action?.()
      dialog.close()
    }
  }

  return (
    <DialogShell
      open={state.isOpen}
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen && !state.isLoading) dialog.close()
      }}
      className="w-[calc(100vw-48px)] max-w-[300px]"
    >
      <div className="p-6 text-center">
        <DialogPrimitives.Title className="text-lg font-semibold text-[#333]">
          {state.dialog.title}
        </DialogPrimitives.Title>

        <DialogPrimitives.Description className="mt-3 whitespace-pre-line text-[#666]">
          {state.dialog.description}
        </DialogPrimitives.Description>

        <Button
          className="relative mt-4 flex items-center justify-center"
          disabled={state.isLoading}
          onClick={handleButtonClick}
        >
          {state.isLoading && (
            <Spinner className="absolute right-7 text-[#176361]" />
          )}
          {state.dialog.button}
        </Button>
      </div>

      {state.dialog.closeButton && !state.isLoading && (
        <button
          className="absolute top-[-10px] right-[-10px] grid size-[42px] place-items-center overflow-hidden rounded-full bg-[rgba(255,255,255,0.56)] backdrop-blur-[37.5px]"
          onClick={() => {
            if (typeof state.dialog.closeButton === 'object') {
              state.dialog.closeButton.action()
            }
            dialog.close()
          }}
        >
          <CloseSvg />
        </button>
      )}
    </DialogShell>
  )
}

function CloseSvg() {
  return (
    <svg
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.21429 4.71442L13.7857 13.2858"
        stroke="#333333"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.21427 13.2858L13.7856 4.71442"
        stroke="#333333"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
